import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Edit2, Trash2, User, Phone, Scissors, FileText, X, ChevronRight, Search, UserPlus, CheckCircle, Calendar, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export default function WorkersPage() {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", specialty: "" });
  const [saving, setSaving] = useState(false);
  
  // Worker detail view
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerSlips, setWorkerSlips] = useState([]);
  const [loadingSlips, setLoadingSlips] = useState(false);

  // Assign-article-from-worker-panel flow
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSlips, setAssignSlips] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await api.get("/workers");
      setWorkers(res.data);
    } catch { toast.error("Failed to load workers"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchWorkers(); }, [fetchWorkers]);

  const fetchWorkerSlips = async (worker) => {
    setSelectedWorker(worker);
    setLoadingSlips(true);
    try {
      // Get all slips and filter by worker name or worker_id (slip-level or item-level)
      const res = await api.get("/slips");
      const filtered = res.data.filter(slip => {
        // Check slip-level worker assignment
        if (slip.worker_id === worker.id || slip.worker_name === worker.name) {
          return true;
        }
        // Check item-level worker assignment
        if (slip.items?.some(item => item.worker_id === worker.id || item.worker_name === worker.name)) {
          return true;
        }
        return false;
      });
      setWorkerSlips(filtered);
    } catch { 
      toast.error("Failed to load worker's slips"); 
      setWorkerSlips([]);
    }
    setLoadingSlips(false);
  };

  const closeWorkerDetail = () => {
    setSelectedWorker(null);
    setWorkerSlips([]);
  };

  // ── Assign an article on any slip to the currently selected worker ──
  const openAssignDialog = async () => {
    if (!selectedWorker) return;
    setAssignOpen(true);
    setAssignSearch("");
    setAssignLoading(true);
    try {
      // Pull open slips only (pending + sent_to_worker + received_from_worker) so admins can't
      // assign onto product_ready / received orders by mistake.
      const [pending, sent, received] = await Promise.all([
        api.get("/slips?status=pending"),
        api.get("/slips?status=sent_to_worker"),
        api.get("/slips?status=received_from_worker"),
      ]);
      const combined = [...pending.data, ...sent.data, ...received.data];
      // Newest first
      combined.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setAssignSlips(combined);
    } catch {
      toast.error("Failed to load slips");
      setAssignSlips([]);
    }
    setAssignLoading(false);
  };

  const filteredAssignSlips = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    if (!q) return assignSlips;
    return assignSlips.filter((s) =>
      (s.slip_number || "").toLowerCase().includes(q) ||
      (s.customer_name || "").toLowerCase().includes(q) ||
      (s.customer_phone || "").toLowerCase().includes(q)
    );
  }, [assignSlips, assignSearch]);

  const assignArticleToWorker = async (slip, itemIndex) => {
    if (!selectedWorker) return;
    setAssignBusy(true);
    try {
      await api.patch(`/slips/${slip.id}/items/${itemIndex}`, { worker_id: selectedWorker.id });
      toast.success(`Assigned Article ${itemIndex + 1} to ${selectedWorker.name}`);
      setAssignOpen(false);
      // Refresh the currently-viewed worker's slip list
      await fetchWorkerSlips(selectedWorker);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
    setAssignBusy(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", phone: "", specialty: "" });
    setDialogOpen(true);
  };

  const openEdit = (w, e) => {
    e?.stopPropagation();
    setEditing(w);
    setForm({ name: w.name, phone: w.phone || "", specialty: w.specialty || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/workers/${editing.id}`, form);
        toast.success("Worker updated");
      } else {
        await api.post("/workers", form);
        toast.success("Worker added");
      }
      setDialogOpen(false);
      fetchWorkers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async (w, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete worker ${w.name}?`)) return;
    try {
      await api.delete(`/workers/${w.id}`);
      toast.success("Worker deleted");
      fetchWorkers();
      if (selectedWorker?.id === w.id) {
        closeWorkerDetail();
      }
    } catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (w, e) => {
    e?.stopPropagation();
    try {
      await api.put(`/workers/${w.id}`, { active: !w.active });
      toast.success(`Worker ${w.active ? "deactivated" : "activated"}`);
      fetchWorkers();
    } catch { toast.error("Failed to update"); }
  };

  // Calculate total articles for a worker from their slips (only articles assigned to this worker)
  const getTotalArticles = () => {
    return workerSlips.reduce((total, slip) => {
      // Count articles explicitly assigned to this worker
      const workerArticles = slip.items?.filter(item => 
        item.worker_id === selectedWorker?.id || item.worker_name === selectedWorker?.name
      ) || [];
      
      // If worker assigned at slip level (not item level), count all articles
      if (workerArticles.length === 0 && (slip.worker_id === selectedWorker?.id || slip.worker_name === selectedWorker?.name)) {
        return total + (slip.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0);
      }
      
      // Count only specifically assigned articles
      return total + workerArticles.reduce((sum, item) => sum + (item.quantity || 1), 0);
    }, 0);
  };

  // Get articles assigned to this worker from a slip
  const getWorkerArticles = (slip) => {
    // Check if any items have this worker assigned
    const workerItems = slip.items?.filter(item => 
      item.worker_id === selectedWorker?.id || item.worker_name === selectedWorker?.name
    ) || [];
    
    // If no items have worker assigned but slip does, return all items
    if (workerItems.length === 0 && (slip.worker_id === selectedWorker?.id || slip.worker_name === selectedWorker?.name)) {
      return slip.items || [];
    }
    
    return workerItems;
  };

  // ─── Month-wise aggregation for the selected worker ───
  // Each slip is placed in the month of the EARLIEST `assigned_at` of this worker's
  // items in that slip. Falls back to slip.created_at when assigned_at is unset
  // (older slips created before per-article assignment tracking was added).
  const workerAnalytics = useMemo(() => {
    if (!selectedWorker || !workerSlips.length) return { months: [], totals: { articles_assigned: 0, articles_received: 0, slips: 0 } };

    const monthToSlips = {};                // "YYYY-MM" → [slip, ...]
    const monthToStats = {};                // "YYYY-MM" → { articles_assigned, articles_received, slips_set }

    const bucketKey = (slip) => {
      const items = getWorkerArticles(slip);
      const assignedTimes = items
        .map(it => it.assigned_at)
        .filter(Boolean)
        .sort();
      const base = assignedTimes[0] || slip.created_at || "";
      return base ? base.slice(0, 7) : "unknown";
    };

    let totalAssigned = 0;
    let totalReceived = 0;

    for (const slip of workerSlips) {
      const key = bucketKey(slip);
      if (!monthToSlips[key]) {
        monthToSlips[key] = [];
        monthToStats[key] = { articles_assigned: 0, articles_received: 0, slips_set: new Set() };
      }
      monthToSlips[key].push(slip);
      monthToStats[key].slips_set.add(slip.id);

      for (const it of getWorkerArticles(slip)) {
        const qty = Number(it.quantity) || 1;
        monthToStats[key].articles_assigned += qty;
        totalAssigned += qty;
        if (it.received_from_worker_at) {
          // Received articles count in the month they were RECEIVED, not assigned.
          const rkey = it.received_from_worker_at.slice(0, 7);
          if (!monthToStats[rkey]) monthToStats[rkey] = { articles_assigned: 0, articles_received: 0, slips_set: new Set() };
          monthToStats[rkey].articles_received += qty;
          totalReceived += qty;
        }
      }
    }

    const sortedKeys = Object.keys(monthToStats).sort((a, b) => b.localeCompare(a));
    const months = sortedKeys.map(key => ({
      key,
      label: key === "unknown" ? "Earlier" : format(parseISO(`${key}-01`), "MMMM yyyy"),
      slips: (monthToSlips[key] || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
      stats: {
        articles_assigned: monthToStats[key].articles_assigned,
        articles_received: monthToStats[key].articles_received,
        slips: monthToStats[key].slips_set.size,
      },
    }));

    return {
      months,
      totals: { articles_assigned: totalAssigned, articles_received: totalReceived, slips: workerSlips.length },
    };
  }, [selectedWorker, workerSlips]);

  const statusLabels = { pending: "Pending", sent_to_worker: "In Progress", product_ready: "Ready", received: "Delivered" };

  return (
    <div data-testid="workers-page" className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in-up">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-semibold text-foreground">Workers</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your tailoring team</p>
        </div>
        <Button data-testid="add-worker-btn" onClick={openAdd} className="btn-maroon rounded-sm gap-2 w-full sm:w-auto">
          <Plus className="w-4 h-4" /> Add Worker
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workers List */}
        <div className={selectedWorker ? "lg:col-span-1" : "lg:col-span-3"}>
          {/* Mobile Cards View */}
          <div className="block md:hidden space-y-3">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-card rounded-sm animate-pulse" />
              ))
            ) : workers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Scissors className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No workers added yet
              </div>
            ) : workers.map((w) => (
              <Card
                key={w.id}
                className={`luxury-card rounded-sm cursor-pointer hover:border-[rgba(212,175,55,0.3)] transition-colors ${selectedWorker?.id === w.id ? 'border-[rgba(212,175,55,0.5)]' : ''}`}
                onClick={() => fetchWorkerSlips(w)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm text-foreground font-medium truncate">{w.name}</p>
                        <button onClick={(e) => toggleActive(w, e)} className="flex-shrink-0">
                          <Badge variant="outline" className={`text-[10px] rounded-sm ${w.active ? "bg-green-500/10 text-green-400 border-green-500/25" : "bg-red-500/10 text-red-400 border-red-500/25"}`}>
                            {w.active ? "Active" : "Inactive"}
                          </Badge>
                        </button>
                      </div>
                      {w.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> {w.phone}</p>}
                      {w.specialty && <p className="text-xs text-muted-foreground/80 mt-0.5">{w.specialty}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" onClick={(e) => openEdit(w, e)} className="h-8 w-8 text-muted-foreground">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => handleDelete(w, e)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="luxury-card rounded-sm hidden md:block">
            <CardContent className="p-0">
              <Table className="luxury-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Name</TableHead>
                    {!selectedWorker && (
                      <>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Phone</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Specialty</TableHead>
                      </>
                    )}
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <TableRow key={i}><TableCell colSpan={selectedWorker ? 3 : 5}><div className="h-8 bg-muted/20 rounded animate-pulse" /></TableCell></TableRow>
                    ))
                  ) : workers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={selectedWorker ? 3 : 5} className="text-center py-12 text-muted-foreground">
                        <Scissors className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        No workers added yet
                      </TableCell>
                    </TableRow>
                  ) : workers.map((w) => (
                    <TableRow 
                      key={w.id} 
                      data-testid={`worker-row-${w.name}`} 
                      className={`border-border cursor-pointer hover:bg-white/[0.02] transition-colors ${selectedWorker?.id === w.id ? 'bg-[rgba(212,175,55,0.05)] border-l-2 border-l-[#D4AF37]' : ''}`}
                      onClick={() => fetchWorkerSlips(w)}
                    >
                      <TableCell className="text-sm font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          {w.name}
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </TableCell>
                      {!selectedWorker && (
                        <>
                          <TableCell className="text-sm text-muted-foreground">{w.phone || "-"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{w.specialty || "-"}</TableCell>
                        </>
                      )}
                      <TableCell>
                        <button onClick={(e) => toggleActive(w, e)}>
                          <Badge variant="outline" className={`text-[10px] rounded-sm cursor-pointer ${w.active ? "bg-green-500/10 text-green-400 border-green-500/25" : "bg-red-500/10 text-red-400 border-red-500/25"}`}>
                            {w.active ? "Active" : "Inactive"}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button data-testid={`edit-worker-${w.name}`} variant="ghost" size="icon" onClick={(e) => openEdit(w, e)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button data-testid={`delete-worker-${w.name}`} variant="ghost" size="icon" onClick={(e) => handleDelete(w, e)} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Worker Detail Panel */}
        {selectedWorker && (
          <div className="lg:col-span-2 space-y-4">
            <Card className="luxury-card rounded-sm">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-serif text-xl">{selectedWorker.name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedWorker.specialty || "General Tailor"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={openAssignDialog}
                    className="btn-maroon rounded-sm gap-2 h-9 text-xs"
                    data-testid="assign-article-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Assign Article
                  </Button>
                  <Button variant="ghost" size="icon" onClick={closeWorkerDetail} className="h-8 w-8 text-muted-foreground">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold text-foreground">{workerSlips.length}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Slips</p>
                  </div>
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold gold-text">{getTotalArticles()}</p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Articles</p>
                  </div>
                  <div className="bg-background rounded-sm p-3 border border-border text-center">
                    <p className="text-2xl font-serif font-bold text-green-400">
                      {workerSlips.filter(s => s.status === 'received').length}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Report / Analytics */}
            {workerSlips.length > 0 && (
              <Card className="luxury-card rounded-sm" data-testid="worker-monthly-report">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 gold-text" />
                    Monthly Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                          <th className="py-2 px-3 sm:px-4 font-medium">Month</th>
                          <th className="py-2 px-3 sm:px-4 font-medium text-right">Slips</th>
                          <th className="py-2 px-3 sm:px-4 font-medium text-right">Articles Assigned</th>
                          <th className="py-2 px-3 sm:px-4 font-medium text-right">Articles Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workerAnalytics.months.map((m) => (
                          <tr key={m.key} className="border-b border-border/40 last:border-b-0" data-testid={`worker-monthly-row-${m.key}`}>
                            <td className="py-2 px-3 sm:px-4 text-foreground">{m.label}</td>
                            <td className="py-2 px-3 sm:px-4 text-right text-foreground">{m.stats.slips}</td>
                            <td className="py-2 px-3 sm:px-4 text-right gold-text font-medium">{m.stats.articles_assigned}</td>
                            <td className="py-2 px-3 sm:px-4 text-right text-emerald-300 font-medium">{m.stats.articles_received}</td>
                          </tr>
                        ))}
                        <tr className="bg-[rgba(255,255,255,0.02)] font-semibold">
                          <td className="py-2.5 px-3 sm:px-4 text-foreground uppercase tracking-wider text-[11px]">Total</td>
                          <td className="py-2.5 px-3 sm:px-4 text-right text-foreground">{workerAnalytics.totals.slips}</td>
                          <td className="py-2.5 px-3 sm:px-4 text-right gold-text">{workerAnalytics.totals.articles_assigned}</td>
                          <td className="py-2.5 px-3 sm:px-4 text-right text-emerald-300">{workerAnalytics.totals.articles_received}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Assigned Slips — grouped by month */}
            <Card className="luxury-card rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 gold-text" />
                  Assigned Slips & Articles
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingSlips ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted/20 rounded animate-pulse" />
                    ))}
                  </div>
                ) : workerSlips.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    No slips assigned to this worker
                  </p>
                ) : (
                  <div className="space-y-6 max-h-[500px] overflow-y-auto pr-1">
                    {workerAnalytics.months.filter(m => m.slips.length > 0).map((m) => (
                      <div key={m.key} data-testid={`worker-month-group-${m.key}`} className="space-y-3">
                        <div className="sticky top-0 bg-card/95 backdrop-blur-sm z-10 flex items-center justify-between gap-3 py-1.5 border-b border-border">
                          <div className="flex items-center gap-2 min-w-0">
                            <Calendar className="w-3.5 h-3.5 gold-text flex-shrink-0" />
                            <h4 className="font-serif text-sm text-foreground truncate">{m.label}</h4>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-shrink-0">
                            <span className="whitespace-nowrap">{m.stats.slips} slip{m.stats.slips === 1 ? "" : "s"}</span>
                            <span className="opacity-30">·</span>
                            <span className="whitespace-nowrap gold-text">{m.stats.articles_assigned} assigned</span>
                            <span className="opacity-30">·</span>
                            <span className="whitespace-nowrap text-emerald-300">{m.stats.articles_received} received</span>
                          </div>
                        </div>
                        {m.slips.map((slip) => (
                          <div
                            key={slip.id}
                            className="bg-background rounded-sm p-4 border border-border hover:border-[rgba(212,175,55,0.3)] cursor-pointer transition-colors"
                            onClick={() => navigate(`/slips/${slip.id}`)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="text-sm font-medium gold-text">{slip.slip_number}</p>
                                <p className="text-xs text-muted-foreground">{slip.customer_name}</p>
                              </div>
                              <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                                {statusLabels[slip.status]}
                              </Badge>
                            </div>
                            {/* Articles List */}
                            <div className="mt-3 space-y-1">
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Assigned Articles:</p>
                              <div className="flex flex-wrap gap-2">
                                {getWorkerArticles(slip).map((item, idx) => {
                                  const received = Boolean(item.received_from_worker_at);
                                  return (
                                    <span
                                      key={idx}
                                      className={`text-xs px-2 py-1 rounded-sm border inline-flex items-center gap-1 ${
                                        received
                                          ? "bg-emerald-950/40 text-emerald-300 border-emerald-700/40"
                                          : "bg-[rgba(114,47,55,0.15)] text-[#D4AF37] border-[rgba(114,47,55,0.2)]"
                                      }`}
                                    >
                                      {received && <CheckCircle className="w-3 h-3" />}
                                      {item.clothing_type?.replace(/_/g, " ")} × {item.quantity || 1}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                              <p className="text-[10px] text-muted-foreground">
                                {slip.created_at ? format(parseISO(slip.created_at), "dd MMM yyyy") : ""}
                              </p>
                              <p className="text-xs text-foreground font-medium">
                                {getWorkerArticles(slip).reduce((sum, item) => sum + (item.quantity || 1), 0) || 0} articles
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">{editing ? "Edit Worker" : "Add Worker"}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {editing ? "Update worker details" : "Add a new worker to your team"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="worker-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Worker name" className="pl-10 bg-background border-border rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="worker-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone number (optional)" className="pl-10 bg-background border-border rounded-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Specialty</Label>
              <div className="relative">
                <Scissors className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="worker-specialty-input" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                  placeholder="e.g. Suits, Shirts, Sherwanis" className="pl-10 bg-background border-border rounded-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-border">Cancel</Button>
            <Button data-testid="save-worker-btn" onClick={handleSave} disabled={saving} className="btn-maroon rounded-sm">
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign-article dialog — pick an open slip then an article */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="assign-article-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">
              Assign an article to <span className="gold-text">{selectedWorker?.name}</span>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Search a slip, then tap the article you want to hand over. Articles already assigned to someone else show that worker&apos;s name — tapping still re-assigns.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              placeholder="Search slip number, customer name, or phone"
              className="pl-10 bg-background border-border rounded-sm"
              data-testid="assign-slip-search"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
            {assignLoading ? (
              [...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted/10 rounded-sm animate-pulse" />)
            ) : filteredAssignSlips.length === 0 ? (
              <p className="text-center py-10 text-muted-foreground text-sm">
                {assignSearch ? "No open slips match your search." : "No open slips available."}
              </p>
            ) : (
              filteredAssignSlips.map((slip) => (
                <div key={slip.id} className="bg-background rounded-sm border border-border p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium gold-text">{slip.slip_number}</p>
                      <p className="text-xs text-muted-foreground">{slip.customer_name} · {slip.customer_phone}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] rounded-sm status-${slip.status}`}>
                      {statusLabels[slip.status] || slip.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(slip.items || []).map((item, idx) => {
                      const isAssignedToThis = item.worker_id === selectedWorker?.id;
                      const isAssignedToOther = Boolean(item.worker_id) && !isAssignedToThis;
                      return (
                        <button
                          key={idx}
                          disabled={assignBusy || isAssignedToThis}
                          onClick={() => assignArticleToWorker(slip, idx)}
                          className={`text-left px-3 py-2 rounded-sm border text-xs transition-colors ${
                            isAssignedToThis
                              ? "border-emerald-700/40 bg-emerald-950/30 text-emerald-300 cursor-default"
                              : isAssignedToOther
                              ? "border-border/60 bg-muted/10 hover:border-[rgba(212,175,55,0.4)] hover:bg-[rgba(114,47,55,0.1)]"
                              : "border-[rgba(212,175,55,0.25)] bg-[rgba(114,47,55,0.08)] hover:bg-[rgba(114,47,55,0.15)]"
                          }`}
                          data-testid={`assign-article-option-${slip.slip_number}-${idx}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="capitalize font-medium">
                              #{idx + 1} · {item.clothing_type?.replace(/_/g, " ")} × {item.quantity || 1}
                            </span>
                            {isAssignedToThis && <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                          </div>
                          {isAssignedToOther && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Currently: {item.worker_name} — tap to re-assign
                            </p>
                          )}
                          {isAssignedToThis && (
                            <p className="text-[10px] mt-1">Already assigned to {selectedWorker.name}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="rounded-sm border-border">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
